<script setup lang="ts">
import type { InsightConfig, InsightConfigMeta } from '../../../models/insightConfig.types'

import { useI18n } from '@/shared/i18n'
import { Input } from '@/shared/ui/input'
import { Select } from '@/shared/ui/select'

const form = defineModel<InsightConfig>('form', { required: true })
const props = defineProps<{
  meta: InsightConfigMeta
  errors: Record<string, string[]>
  disabled?: boolean
}>()
const { t } = useI18n()

function update<K extends keyof InsightConfig>(field: K, value: InsightConfig[K]): void {
  form.value = { ...form.value, [field]: value }
}
function errorFor(field: keyof InsightConfig): string {
  return (props.errors[String(field)] ?? [])
    .map((message) => (message.startsWith('insights.') ? t(message) : message))
    .join(' ')
}
</script>

<template>
  <fieldset class="config-section grid gap-md min-w-0 p-lg border border-border rounded-lg">
    <legend>{{ t('insights.sysadmin.config.section.classification') }}</legend>
    <div class="config-section__grid grid grid-cols-2 gap-md max-md:grid-cols-[minmax(0,_1fr)]">
      <Select
        :model-value="form.outcome_category"
        :label="t('insights.sysadmin.config.fields.outcome')"
        :options="meta.outcome_category_choices"
        :disabled="disabled"
        @update:model-value="$event !== null && update('outcome_category', String($event))"
      />
      <Select
        :model-value="form.severity"
        :label="t('insights.sysadmin.config.fields.severity')"
        :options="meta.severity_choices"
        :disabled="disabled"
        @update:model-value="$event !== null && update('severity', String($event))"
      />
      <Select
        :model-value="form.confidence"
        :label="t('insights.sysadmin.config.fields.confidence')"
        :options="meta.confidence_choices"
        :disabled="disabled"
        @update:model-value="$event !== null && update('confidence', String($event))"
      />
      <Input
        :model-value="Array.isArray(form.platform) ? form.platform.join(', ') : form.platform"
        :label="t('insights.sysadmin.config.fields.platform')"
        :disabled="disabled"
        :error="Boolean(errorFor('platform'))"
        :error-message="errorFor('platform')"
        @update:model-value="update('platform', String($event))"
      />
    </div>
    <p class="config-section__hint m-0 text-muted text-[0.75rem]">
      {{ t('insights.sysadmin.config.platformMetaRisk') }}
    </p>
  </fieldset>
</template>

<style scoped>
.config-section legend {
  padding-inline: var(--ui-space-xs);
  font-weight: 600;
}
</style>
